import { TrendingUp } from "lucide-react";
import { Users as AreaClienteIcon } from "lucide-react";
/**
 * Catálogo canônico de módulos do SIMPLER.
 * Fonte única para: Sidebar, CommandPalette, ComingSoon pages, PRD.
 *
 * Convenções:
 * - `available: true` → módulo em produção; ao clicar navega para `path`
 * - `available: false` → módulo v2; navegação abre página ComingSoon
 * - `requiredPermission`: permission key (ex: "empresas.view") — sidebar filtra por isso
 * - `moduleSlug`: slug da permissão de módulo (default = slug do próprio item)
 */

import {
  LayoutDashboard,
  BellRing,
  MessageSquare,
  Flame,
  Building2,
  Compass,
  Contact2,
  FileCheck2,
  Wallet,
  Landmark,
  CalendarDays,
  ClipboardList,
  Coins,
  Scale,
  FileSpreadsheet,
  BookOpenCheck,
  BarChart3,
  Sparkles,
  Headphones,
  FileText,
  Zap,
  Plug,
  Settings,
  ShieldCheck, WalletCards,
  Activity,
  ScrollText,
  Receipt,
  Users,
  KeyRound,
  Radar,
  PlayCircle,
  Trophy,
} from "lucide-react";

export const MODULES = [
  // ============ INÍCIO ============
  {
    slug: "dashboard",
    label: "Dashboard Geral",
    icon: LayoutDashboard,
    path: "/app",
    exact: true,
    group: "Início",
    available: true,
    testId: "nav-dashboard",
    description: "Visão consolidada do escritório em tempo real.",
  },
  {
    slug: "area-do-cliente",
    label: "Área do Cliente",
    icon: AreaClienteIcon,
    path: "/app/area-do-cliente",
    group: "Automação",
    available: true,
    testId: "nav-area-do-cliente",
    description: "Portal para seus clientes baixarem guias, comprovantes e certidões.",
  },
  {
    slug: "primeiros-passos",
    label: "Primeiros passos",
    icon: PlayCircle,
    path: "/app/primeiros-passos",
    group: "Início",
    available: true,
    testId: "nav-primeiros-passos",
    description: "Aulas em vídeo para começar a usar a Simpler.",
  }, // PP4
  {
    slug: "jornada-simpler",
    label: "Intensivão Simpler",
    icon: Trophy,
    path: "/app/intensivao",
    group: "Início",
    available: true,
    testId: "nav-jornada-simpler",
    description: "10 dias de aula, prática e checagem para dominar 100% da Simpler.",
  }, // PP11
  {
    slug: "dashboard-federal",
    label: "Dashboard Federal",
    icon: Landmark,
    path: "/app/dashboard-federal",
    group: "Início",
    available: true,
    testId: "nav-dashboard-federal",
    description: "Visão centralizada das obrigações, pendências e serviços federais.",
  },
  {
    slug: "simples-nacional-apuracao",
    label: "Simples Nacional",
    icon: Coins,
    path: "/app/simples-nacional/apuracao",
    group: "Início",
    available: true,
    testId: "nav-simples-nacional-apuracao",
    description: "Apuração mensal, recuperação tributária e histórico PGDAS-D.",
  },
  {
    slug: "alertas",
    label: "Central de Alertas",
    icon: BellRing,
    path: "/app/alertas",
    group: "Início",
    available: true,
    testId: "nav-alertas",
    description: "Notificações internas priorizadas por severidade.",
  },
  {
    slug: "prioridades",
    label: "Central de Prioridades",
    icon: Flame,
    path: "/app/prioridades",
    group: "Início",
    available: true,
    testId: "nav-prioridades",
    description: "Mensagens críticas + alertas ativos consolidados por urgência.",
  },
  {
    slug: "mensagens",
    label: "Mensagens",
    icon: MessageSquare,
    path: "/app/mensagens",
    group: "Início",
    available: true,
    testId: "nav-mensagens",
    description: "Caixa Postal Fiscal — mensagens do e-CAC, PGFN, DCTFWeb e Simples classificadas por IA.",
  },

  // ============ ADMINISTRAÇÃO › CADASTROS ============
  {
    slug: "empresas",
    label: "Empresas",
    icon: Building2,
    path: "/app/empresas",
    group: "Administração",
    subgroup: "Cadastros",
    available: true,
    testId: "nav-empresas",
    description: "CRUD dos CNPJs monitorados.",
  },
  {
    slug: "procuradores",
    label: "Procuradores",
    icon: ShieldCheck,
    path: "/app/procuradores",
    group: "Administração",
    subgroup: "Cadastros",
    available: true,
    testId: "nav-procuradores",
    requiredPermission: "empresas.view",
    description: "Cadastro dos escritórios procuradores e vínculo com as empresas monitoradas.",
  },
  {
    slug: "usuarios",
    label: "Usuários",
    icon: Users,
    path: "/app/usuarios",
    group: "Administração",
    subgroup: "Cadastros",
    available: true,
    testId: "nav-usuarios",
    moduleSlug: "usuarios",
    requiredPermission: "usuarios.view",
    description: "Gestão dos usuários do escritório com atribuição de empresas.",
  },
  {
    slug: "gerenciar-empresas",
    label: "Gerenciar Empresas",
    icon: FileSpreadsheet,
    path: "/app/gerenciar-empresas",
    group: "Administração",
    subgroup: "Cadastros",
    available: true,
    testId: "nav-gerenciar-empresas",
    requiredPermission: "empresas.create",
    description: "Importação em massa, ações administrativas e sincronização de CNPJs.",
  },

  // ============ GESTÃO ============
  {
    slug: "painel-360",
    label: "Painel 360°",
    icon: Compass,
    path: "/app/painel-360",
    group: "Gestão",
    available: true,
    testId: "nav-painel-360",
    requiredPermission: "diagnostico.view",
    description: "Visão executiva consolidada de indicadores, ausências, diagnósticos e riscos.",
    long: "Central executiva da carteira com drill-down por empresa e dados persistidos dos módulos fiscais.",
    features: [
      "Grid comparativo de todas empresas com mini-cards de Health Score",
      "Ranking por criticidade / pendências / débitos",
      "Filtros por regime, UF e status",
      "Export do grid para PDF/CSV",
      "Atalho para o Painel 360° individual",
    ],
  },
  {
    slug: "clientes",
    label: "Clientes",
    icon: Contact2,
    path: "/app/clientes",
    group: "Gestão",
    available: false,
    testId: "nav-clientes",
    description: "Gestão da carteira comercial do escritório (pagantes).",
    long: "Não confunda com Empresas — aqui estão os clientes-pagantes do seu escritório (contadores gerenciando sua carteira comercial). Empresas são os CNPJs monitorados por conta desses clientes.",
    features: [
      "Cadastro de cliente (PF ou PJ)",
      "Contrato ativo (valor, vigência, forma de pagamento)",
      "Vínculo 1:N com Empresas monitoradas",
      "NPS e histórico de satisfação",
      "Faturamento por cliente (integração futura Stripe/Asaas)",
      "Portal do cliente (fase futura)",
    ],
    eta: "Q2 2026",
  },

  // ============ FISCAL ============
  {
    slug: "certidoes",
    label: "Certidões",
    icon: FileCheck2,
    path: "/app/certidoes",
    group: "Fiscal",
    available: true,
    testId: "nav-certidoes",
    description: "Central única de certidões negativas com alertas de vencimento.",
    long: "CND, CRF, CNDT, Certidão Municipal, Estadual — tudo consolidado por empresa, com alerta 15 dias antes do vencimento.",
    features: [
      "Grid unificado de todas certidões",
      "Filtro por status (válida / vence em 15d / vencida)",
      "Upload de PDF da certidão (Object Storage)",
      "Alerta automático 15 dias antes do vencimento (cronjob já ativo)",
      "Renovação assistida com deep link para o portal correto",
      "Histórico completo (versões anteriores)",
    ],
    eta: "Q1 2026",
  },
  {
    slug: "parcelamentos",
    label: "Parcelamentos",
    icon: Landmark,
    path: "/app/parcelamentos",
    group: "Fiscal",
    available: true,
    testId: "nav-parcelamentos",
    description: "Parcelamentos ativos com próxima parcela e saldo devedor.",
    features: [
      "Grid unificado de todos parcelamentos",
      "Alerta 5 dias antes de cada parcela",
      "Simulação de RRR (redução com quitação antecipada)",
      "Cronograma completo por parcelamento",
      "Vínculo com débito de origem",
    ],
    eta: "Q2 2026",
  },
  {
    slug: "agenda",
    label: "Agenda Fiscal",
    icon: CalendarDays,
    path: "/app/agenda",
    group: "Fiscal",
    available: true,
    testId: "nav-agenda",
    description: "Calendário unificado de obrigações, certidões e prazos.",
    long: "Todos os prazos do escritório em um só calendário. Zero dependência de agenda de papel ou planilha.",
    features: [
      "Vistas mês / semana / lista",
      "Filtro por empresa, tipo, responsável",
      "Sync com Google Calendar",
      "Export .ics",
      "Templates de obrigações recorrentes (DCTFWeb mensal, ECD anual, etc.)",
    ],
    eta: "Q1 2026",
  },
  {
    slug: "obrigacoes",
    label: "Obrigações",
    icon: ClipboardList,
    path: "/app/obrigacoes",
    group: "Fiscal",
    available: true,
    testId: "nav-obrigacoes",
    description: "Controle detalhado de obrigações acessórias.",
    features: [
      "Catálogo (DCTFWeb, EFD-Contribuições, EFD-Reinf, ECD, ECF, DIRF, DASN)",
      "Status por competência (pendente / entregue / retificada)",
      "Checklist prévio à entrega",
      "Anexo do arquivo transmitido (recibo)",
      "Comparativo entre declarações relacionadas",
    ],
    eta: "Q2 2026",
  },

  // ============ PORTAIS ============
  {
    slug: "simples-nacional",
    label: "Simples Nacional",
    icon: Coins,
    path: "/app/simples-nacional",
    group: "Portais",
    available: true,
    testId: "nav-simples-nacional",
    description: "Monitoramento específico do Simples Nacional.",
    long: "Termômetro de faturamento, alerta de proximidade do sublimite, DAS por mês, opção/exclusão/reinclusão.",
    features: [
      "Termômetro faturamento vs sublimite (R$ 4,8M anual)",
      "Alerta em 80% / 90% / 100% do limite",
      "Histórico de opção / exclusão / reinclusão",
      "Termo de Exclusão detectado com prazo de defesa",
      "DAS: emissão e status por competência",
    ],
    eta: "Q2 2026",
  },
  {
    slug: "simples-nacional-das",
    label: "Gestão do DAS",
    icon: Receipt,
    path: "/app/simples-nacional/das",
    group: "Portais",
    available: true,
    testId: "nav-simples-das",
    description: "Emita, recalcule, baixe e envie DAS via canal oficial da Receita Federal.",
    long: "Painel completo de gestão do DAS: dashboard de vencidos/em aberto/pagos, tabela por competência com juros/multa/valor atualizado, ações (emitir, recalcular, PDF, enviar cliente, marcar pago) — tudo via canal oficial da Receita Federal. Zero cálculo manual ou automação de navegador.",
    features: [
      "Dashboard: em aberto, vencidos, pagos, valor total, empresas inadimplentes",
      "Tabela por competência com juros/multa/valor atualizado",
      "Emitir + Recalcular + Segunda via (todos via canal oficial da Receita Federal)",
      "Baixar PDF da guia",
      "Registrar envio ao cliente + Marcar como pago",
      "Auditoria completa (usuário/data/ação/status)",
      "Sisi analisa DAS vencidos e recomenda ação",
    ],
  },
  {
    slug: "dctfweb",
    label: "DCTFWeb",
    icon: FileSpreadsheet,
    path: "/app/dctfweb",
    group: "Portais",
    available: true,
    testId: "nav-dctfweb",
    description: "Controle de DCTFWeb por competência.",
    features: [
      "Status por competência (rascunho / transmitida / retificada / rejeitada)",
      "Comparativo EFD-Reinf vs DCTFWeb",
      "Alertas de divergência antes da transmissão",
      "Cronograma de envio",
      "Import de XML transmitido",
    ],
    eta: "Q2 2026",
  },
  {
    slug: "reforma-tributaria",
    label: "Simulador Tributário",
    icon: BookOpenCheck,
    path: "/app/health-score?tab=simulator",
    tab: "simulator",
    group: "Portais",
    available: true,
    testId: "nav-reforma-tributaria",
    description: "Simulador de impacto da Reforma Tributária (CBS + IBS).",
    long: "Módulo dedicado à Reforma Tributária. Simulador de carga tributária no regime atual vs pós-reforma, cronograma de transição 2026–2033, educação e IA para dúvidas técnicas.",
    features: [
      "Simulador de carga tributária (regime atual vs pós-reforma)",
      "Cronograma de transição (2026–2033)",
      "Recomendações por regime tributário",
      "Análise por CNAE",
      "Glossário + artigos + IA respondendo dúvidas técnicas",
      "Alertas legislativos (LC 214, decretos)",
    ],
    eta: "Q3 2026",
  },

  // ============ INTELIGÊNCIA ============
  {
    slug: "diagnostico",
    label: "Raio X Simpler",
    icon: ScrollText,
    path: "/app/diagnostico",
    group: "Inteligência",
    available: true,
    testId: "nav-diagnostico",
    description: "Diagnóstico executivo consolidado da carteira com Health Score, empresas em risco, exposição financeira e IA.",
    long: "Consolidação executiva de toda a carteira do escritório: Health Score, ranking de empresas críticas, exposição em PGFN/RFB, agenda dos próximos 30 dias e análise automática da Sisi. Exportação em PDF profissional.",
    features: [
      "Health Score da carteira (0-100)",
      "Ranking Top empresas em risco",
      "Exposição consolidada (PGFN, RFB, Parcelamentos)",
      "Agenda dos próximos 30 dias",
      "Insights automáticos da Sisi",
      "Exportação PDF profissional",
    ],
  },
  // H17: abas da página de análise como itens de menu
  {
    slug: "pre-analise",
    label: "Triagem Simpler",
    icon: ScrollText,
    path: "/app/health-score?tab=pre",
    tab: "pre",
    group: "Inteligência",
    available: true,
    testId: "nav-pre-analise",
    description: "Triagem Simpler fiscal da empresa.",
    long: "Triagem Simpler fiscal da empresa com base nos dados persistidos da Simpler.",
    features: [],
  },
  {
    slug: "pos-analise",
    label: "Evolução Simpler",
    icon: ScrollText,
    path: "/app/health-score?tab=post",
    tab: "post",
    group: "Inteligência",
    available: true,
    testId: "nav-pos-analise",
    description: "Evolução Simpler fiscal com a Sisi.",
    long: "Evolução Simpler fiscal da empresa com a Sisi, sua analista fiscal inteligente.",
    features: [],
  },
  {
    slug: "health-score",
    label: "Saúde Fiscal",
    icon: ScrollText,
    path: "/app/health-score",
    tab: "health",
    group: "Inteligência",
    available: true,
    testId: "nav-health-score",
    description: "Saúde fiscal individual por empresa com score, pendências e checklist operacional.",
    long: "Saúde Financeira da empresa baseada em dados fiscais reais da Simpler, com regularidades, pendências, indisponibilidades, fontes da análise e plano de ação.",
    features: [
      "Saúde Financeira de 0 a 100",
      "Checklist fiscal por categoria",
      "Regularidades e pendências",
      "Itens indisponíveis identificados",
      "Triagem Simpler Fiscal",
      "Plano de Ação Priorizado",
    ],
  },
  {
    slug: "bi-ausencias",
    label: "BI Ausências",
    icon: BarChart3,
    path: "/app/bi/ausencias",
    group: "Inteligência",
    available: true,
    testId: "nav-bi-ausencias",
    description: "Ausências de declarações, riscos e prioridades da carteira.",
  },
  {
    slug: "bi-diagnosticos",
    label: "BI Diagnósticos",
    icon: BarChart3,
    path: "/app/bi/diagnosticos",
    group: "Inteligência",
    available: true,
    testId: "nav-bi-diagnosticos",
    description: "Diagnóstico fiscal e análise da saúde da carteira.",
  },
  {
    slug: "bi",
    label: "Indicadores",
    icon: BarChart3,
    path: "/app/bi/indicadores",
    group: "Inteligência",
    available: true,
    testId: "nav-bi",
    description: "Indicadores fiscais e operacionais consolidados da carteira.",
    long: "Business Intelligence 100% fiscal. Séries temporais, comparativos históricos (período atual vs anterior), breakdowns por regime tributário e ranking de empresas com maior exposição.",
    features: [
      "Séries temporais de débitos, DCTFWeb, certidões",
      "Comparativos histórico (30/60/90d vs período anterior)",
      "Filtros por regime tributário e período (7d a 24 meses)",
      "Ranking Top 10 PGFN e distribuição SN",
      "Exportação PDF e Excel",
      "Insights de IA por filtro",
    ],
  },
  {
    slug: "ia",
    label: "Sisi",
    icon: Sparkles,
    path: "/app/sisi",
    group: "Inteligência",
    available: true,
    testId: "nav-sisi",
    description: "Sua Analista Fiscal Inteligente — acompanha a carteira, identifica riscos, prioridades e informações fiscais importantes.",
  },

  // ============ OPERAÇÃO ============
    {
    slug: "relatorios",
    label: "Relatórios",
    icon: FileText,
    path: "/app/relatorios",
    group: "Operação",
    available: true,
    testId: "nav-relatorios",
    description: "Relatórios profissionais em PDF e Excel gerados a partir dos dados sincronizados.",
    long: "Wizard de geração de relatórios: escolha o tipo (Carteira, Painel 360°, Certidões, Débitos, Parcelamentos, DCTFWeb, PGFN, SN, Agenda) e filtre por empresa, regime tributário e período. Insights da SIMPLER AI incluídos no PDF.",
    features: [
      "9 tipos de relatório (Carteira, Painel 360°, Certidões, Débitos, Parcelamentos, DCTFWeb, SN, PGFN, Agenda)",
      "Filtros: empresa, regime tributário, período",
      "Formatos: PDF profissional e Excel (XLSX)",
      "Insights da Sisi no PDF (opcional)",
      "Prévia do dataset antes de gerar",
    ],
  },
  {
    slug: "automacoes",
    label: "Automações",
    icon: Zap,
    path: "/app/automacoes",
    group: "Operação",
    available: true,
    testId: "nav-automacoes",
    moduleSlug: "automacoes",
    requiredPermission: "automacoes.view",
    description: "Cronjobs em background que substituem checagens manuais.",
  },
  {
    slug: "integracoes",
    label: "Sincronização",
    icon: Plug,
    path: "/app/integracoes",
    group: "Operação",
    available: true,
    platformOnly: true,
    testId: "nav-integracoes",
    moduleSlug: "plataforma_saude",
    requiredPermission: "plataforma_saude.view",
    description: "Backbone técnico de sincronização — visão exclusiva do platform_owner.",
  },
  {
    slug: "integracoes-scheduler",
    label: "Fila de Sincronização",
    icon: Activity,
    path: "/app/integracoes/scheduler",
    group: "Operação",
    available: true,
    platformOnly: true,
    testId: "nav-scheduler",
    moduleSlug: "plataforma_saude",
    requiredPermission: "plataforma_saude.view",
    description: "Status técnico do motor de sincronização — platform_owner apenas.",
  },
  {
    slug: "serpro-caixa-postal",
    label: "Caixa Postal (avançado)",
    icon: MessageSquare,
    path: "/app/serpro/caixa-postal",
    group: "Operação",
    available: true,
    testId: "nav-serpro-caixa-postal",
    moduleSlug: "plataforma_saude",
    requiredPermission: "plataforma_saude.view",
    description: "Interface técnica — platform_owner apenas.",
  },
  {
    slug: "situacao-fiscal",
    label: "Situação Fiscal",
    icon: ShieldCheck,
    path: "/app/serpro/sitfis",
    group: "Fiscal",
    available: true,
    testId: "nav-situacao-fiscal",
    moduleSlug: "situacao_fiscal",
    requiredPermission: "situacao_fiscal.view",
    description: "Situação fiscal consolidada por empresa — solicitar protocolo e emitir relatório completo.",
  },

  // ============ SISTEMA ============
  {
    slug: "perfis-permissoes",
    label: "Perfis e Permissões",
    icon: ShieldCheck,
    path: "/app/perfis-permissoes",
    group: "Sistema",
    available: true,
    testId: "nav-perfis-permissoes",
    moduleSlug: "perfis_permissoes",
    requiredPermission: "perfis_permissoes.view",
    description: "Customize permissões por papel (matriz módulo × ação).",
  },
  {
    slug: "auditoria",
    label: "Auditoria",
    icon: ScrollText,
    path: "/app/auditoria",
    group: "Sistema",
    available: true,
    testId: "nav-auditoria",
    moduleSlug: "auditoria",
    requiredPermission: "auditoria.view",
    description: "Trilha de auditoria completa (LGPD) — filtre por usuário, ação, empresa e data.",
  },
  {
    slug: "api-keys",
    label: "API Keys",
    icon: KeyRound,
    path: "/app/api-keys",
    group: "Sistema",
    available: true,
    testId: "nav-api-keys",
    moduleSlug: "api_keys",
    requiredPermission: "api_keys.view",
    description: "Chaves de API públicas do tenant com escopos e rate limit.",
  },
  {
    slug: "meu-plano",
    label: "Meu Plano",
    icon: Settings,
    path: "/app/meu-plano",
    group: "Sistema",
    available: true,
    testId: "nav-meu-plano",
    moduleSlug: "configuracoes",
    requiredPermission: "configuracoes.view",
    description: "Plano, limites, consumo, cobranças, notas fiscais e contrato do escritório.",
  },
  {
    slug: "configuracoes",
    label: "Configurações",
    icon: Settings,
    path: "/app/configuracoes",
    group: "Sistema",
    available: true,
    testId: "nav-configuracoes",
    moduleSlug: "configuracoes",
    requiredPermission: "configuracoes.view",
    description: "Hub de administração: escritório, perfil, segurança, integrações SERPRO.",
  },
  {
    slug: "plataforma-saude",
    label: "Saúde da plataforma",
    icon: Radar,
    path: "/app/plataforma-saude",
    group: "Plataforma",
    available: true,
    testId: "nav-plataforma-saude",
    moduleSlug: "plataforma_saude",
    requiredPermission: "plataforma_saude.view",
    platformOnly: true,
    description: "Painel exclusivo do platform_owner — status do backbone SERPRO da plataforma.",
  },
  {
    slug: "plataforma-tenants",
    label: "Escritórios",
    icon: ShieldCheck,
    path: "/app/plataforma-tenants",
    group: "Plataforma",
    available: true,
    testId: "nav-plataforma-tenants",
    moduleSlug: "plataforma_saude",
    requiredPermission: "plataforma_saude.view",
    platformOnly: true,
    description: "Gestão global de todos os escritórios cadastrados no SIMPLER — exclusivo platform_owner.",
  },
  {
    slug: "plataforma-ativacao",
    label: "Ativação de clientes",
    icon: Activity,
    path: "/app/plataforma-ativacao",
    group: "Plataforma",
    available: true,
    testId: "nav-plataforma-ativacao",
    moduleSlug: "plataforma_saude",
    requiredPermission: "plataforma_saude.view",
    platformOnly: true,
    description: "Uso real e risco de cancelamento de cada escritório.",
  }, // PP10
  {
    slug: "plataforma-resultados",
    label: "Resultados",
    icon: TrendingUp,
    path: "/app/plataforma-resultados",
    group: "Plataforma",
    available: true,
    testId: "nav-plataforma-resultados",
    moduleSlug: "plataforma_saude",
    requiredPermission: "plataforma_saude.view",
    platformOnly: true,
    description: "Resultado mensal, pró-labore e fechamento da Simpler.",
  },
  {
    slug: "plataforma-financeiro",
    label: "Cobranças",
    icon: WalletCards,
    path: "/app/plataforma-financeiro",
    group: "Plataforma",
    available: true,
    testId: "nav-plataforma-financeiro",
    moduleSlug: "plataforma_saude",
    requiredPermission: "plataforma_saude.view",
    platformOnly: true,
    description: "Central financeira da Simpler — cobranças, pagamentos e integração Asaas.",
  },
  {
    slug: "plataforma-atendimento",
    label: "Atendimento",
    icon: Headphones,
    path: "/app/plataforma-atendimento",
    group: "Plataforma",
    available: true,
    testId: "nav-plataforma-atendimento",
    moduleSlug: "plataforma_saude",
    requiredPermission: "plataforma_saude.view",
    platformOnly: true,
    description: "Central interna de atendimento da Simpler para acompanhar, responder e encerrar solicitações dos escritórios.",
  },
  {
    slug: "plataforma-serpro",
    label: "Configuração das APIs",
    icon: Settings,
    path: "/app/plataforma-serpro",
    group: "Plataforma",
    available: true,
    testId: "nav-plataforma-serpro",
    moduleSlug: "plataforma_saude",
    requiredPermission: "plataforma_saude.view",
    platformOnly: true,
    description: "Configurações globais das 3 APIs SERPRO (Integra Contador, CND, Dívida Ativa) — exclusivo platform_owner.",
  },
{
    slug: "plataforma-serpro-usage",
    label: "Consumo Serpro",
    icon: BarChart3,
    path: "/app/plataforma/consumo-serpro",
    group: "Plataforma",
    available: true,
    testId: "nav-plataforma-serpro-usage",
    platformOnly: true,
    description: "Consumo e custo estimado das chamadas SERPRO",
  },
  {
    slug: "plataforma-inicio",
    label: "Início",
    icon: LayoutDashboard,
    path: "/app/plataforma-inicio",
    group: "Plataforma",
    available: true,
    platformOnly: true,
    testId: "nav-plataforma-inicio",
    description: "Pendências do dia e números do negócio — exclusivo platform_owner.",
  },
  {
    slug: "plataforma-portal-comercial",
    label: "Portal Comercial",
    icon: Contact2,
    path: "/commercial-portal",
    group: "Plataforma",
    available: true,
    platformOnly: true,
    testId: "nav-plataforma-portal-comercial",
    description: "Consultores, contratos, comissões e ranking — exclusivo platform_owner.",
  },
];

export const GROUPS = ["Início", "Administração", "Gestão", "Fiscal", "Portais", "Inteligência", "Operação", "Sistema", "Plataforma"];

export const modulesByGroup = () => {
  const map = {};
  for (const g of GROUPS) map[g] = [];
  for (const m of MODULES) map[m.group].push(m);
  return map;
};

export const moduleBySlug = (slug) => MODULES.find((m) => m.slug === slug);
